import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useAuth } from '../auth/AuthAllapot.tsx';
import { formatDatumIdo, formatFt } from '../lib/formaz.ts';
import { supabase } from '../lib/supabase.ts';
import { ELVESZETT_OK_CIMKE, MEGBIZAS_TIPUS_CIMKE, STATUSZ_CIMKE, type LeadStatusz } from '../lib/tipusok.ts';
import { Gomb, HibaUzenet, Kartya, Mezo, Valaszto } from '../ui/elemek.tsx';
import type { LeadSor } from './AdatokPanel.tsx';
import { VALASZTHATO_STATUSZOK, statuszFrissites, statuszValtasHiba, type StatuszMezok } from './statusz.ts';
import { esedekes, folyoHonap, honapCimke, valaszthatoHonapok } from './ujrahivas.ts';

/** `datetime-local` érték (a böngésző helyi ideje) → ISO. Érvénytelen vagy üres → null. */
function helyiBolIso(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function isoBolHelyi(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function szamBol(v: string): number | null {
  const n = Number(v.replace(/\D/g, ''));
  return n > 0 ? n : null;
}

/** A leadhez már rögzített státuszrészletek: a mentés után is látszanak, nem csak az űrlapmezőben. */
function rogzitettReszletek(lead: LeadSor): { cimke: string; ertek: string }[] {
  const sorok: { cimke: string; ertek: string }[] = [];
  if (lead.statusz === 'szemle_egyeztetve' && lead.szemle_idopont) sorok.push({ cimke: 'Szemle időpontja', ertek: formatDatumIdo(lead.szemle_idopont) });
  if (lead.statusz === 'kesobb_aktualis' && lead.ujrahivas_honap) {
    const ertek = honapCimke(lead.ujrahivas_honap);
    sorok.push({ cimke: 'Újrahívás', ertek: esedekes(lead.ujrahivas_honap, Date.now()) ? `${ertek} (esedékes)` : ertek });
  }
  if ((lead.statusz === 'megbizas' || lead.statusz === 'eladva') && lead.megbizas_tipus) {
    sorok.push({ cimke: 'Megbízás típusa', ertek: MEGBIZAS_TIPUS_CIMKE[lead.megbizas_tipus] ?? lead.megbizas_tipus });
  }
  if (lead.statusz === 'eladva' && lead.tenyleges_ar) sorok.push({ cimke: 'Tényleges eladási ár', ertek: formatFt(lead.tenyleges_ar) });
  if (lead.statusz === 'elveszett' && lead.elveszett_ok) sorok.push({ cimke: 'Elvesztés oka', ertek: ELVESZETT_OK_CIMKE[lead.elveszett_ok] ?? lead.elveszett_ok });
  return sorok;
}

/**
 * A `mentve` a szülőben él: státuszváltáskor a komponens újra felépül (a kulcsában a státusz is benne van), a saját
 * állapota elveszne, és a mentés visszajelzés nélkül maradna.
 */
export default function StatuszValto({ lead, mentve, onMentve }: { lead: LeadSor; mentve: boolean; onMentve(mentve: boolean): void }) {
  const { session } = useAuth();
  const qc = useQueryClient();
  const [cel, setCel] = useState<LeadStatusz>(lead.statusz);
  const [szemle, setSzemle] = useState(isoBolHelyi(lead.szemle_idopont));
  const [megbizas, setMegbizas] = useState<StatuszMezok['megbizasTipus']>(
    lead.megbizas_tipus === 'kizarolagos' || lead.megbizas_tipus === 'nem_kizarolagos' ? lead.megbizas_tipus : null);
  const [ar, setAr] = useState(lead.tenyleges_ar ? lead.tenyleges_ar.toLocaleString('hu-HU') : '');
  const [ok, setOk] = useState(lead.elveszett_ok ?? '');
  // A lejárt hónap nem választható újra (az adatbázis sem fogadja el), ezért az űrlap üresen indul.
  const [honap, setHonap] = useState(lead.ujrahivas_honap && lead.ujrahivas_honap >= folyoHonap(Date.now()) ? lead.ujrahivas_honap : '');
  const [megjegyzes, setMegjegyzes] = useState('');
  const [mezoHiba, setMezoHiba] = useState<string | null>(null);

  const mentes = useMutation({
    mutationFn: async (m: StatuszMezok) => {
      const { error } = await supabase.from('leads').update(statuszFrissites(cel, m)).eq('id', lead.id);
      if (error) throw error;
      if (cel === 'elveszett' && megjegyzes.trim()) {
        const { error: e2 } = await supabase.from('lead_esemenyek').insert({
          lead_id: lead.id, office_id: lead.office_id, tipus: 'jegyzet',
          adat: { szoveg: megjegyzes.trim() }, szerzo: session?.user.id ?? null,
        });
        if (e2) throw e2;
      }
    },
    onSuccess: async () => {
      setMegjegyzes('');
      onMentve(true);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['lead', lead.id] }),
        qc.invalidateQueries({ queryKey: ['esemenyek', lead.id] }),
        qc.invalidateQueries({ queryKey: ['leadek'] }),
      ]);
    },
  });

  if (lead.statusz === 'nem_ellenorzott') {
    return (
      <Kartya>
        <h2 className="mb-2 text-[17px] font-medium tracking-[-0.015em]">Státusz</h2>
        <p className="text-szoveg-halk">A telefonszám nincs megerősítve, ezért a státusz nem módosítható.</p>
      </Kartya>
    );
  }

  const reszletek = rogzitettReszletek(lead);

  function mentesGomb() {
    const m: StatuszMezok = { szemleIdopont: helyiBolIso(szemle), megbizasTipus: megbizas, tenylegesAr: szamBol(ar), elveszettOk: ok || null, ujrahivasHonap: honap || null };
    const hiba = statuszValtasHiba(cel, m);
    setMezoHiba(hiba);
    onMentve(false);
    if (!hiba) mentes.mutate(m);
  }

  // Az „Új” és az „Elérve” köztes állapot: a hívás után mindig tovább kell lépni (lezárás, szemle, Később aktuális).
  const teendo = lead.statusz === 'uj' || lead.statusz === 'elerve';
  return (
    <Kartya className={teendo ? 'statusz-teendo' : ''}>
      <h2 className="mb-3 text-[17px] font-medium tracking-[-0.015em]">Státusz</h2>
      {teendo && (
        <p data-testid="statusz-teendo" className="mb-3 rounded-xl bg-kiemelt-halvany px-3.5 py-2.5 text-sm font-medium text-kiemelt">
          A beszélgetés után mindig állítsd be a státuszt. Ha nem akar eladni: Elveszett, „Csak érdeklődött”. Az elemzés és az újrahívási lista a státuszból számol, a jegyzetből nem.
        </p>
      )}
      <div className="grid gap-3">
        {reszletek.length > 0 && (
          <dl data-testid="statusz-reszletek" className="grid gap-1 rounded-xl border border-vonal px-3.5 py-2.5">
            {reszletek.map((r) => (
              <div key={r.cimke} className="flex flex-wrap justify-between gap-x-3">
                <dt className="text-sm text-szoveg-halk">{r.cimke}</dt>
                <dd className="font-medium">{r.ertek}</dd>
              </div>
            ))}
          </dl>
        )}
        <Valaszto label="Státusz" value={cel} onChange={(e) => { setCel(e.target.value as LeadStatusz); setMezoHiba(null); onMentve(false); }}>
          {VALASZTHATO_STATUSZOK.map((s) => <option key={s} value={s}>{STATUSZ_CIMKE[s]}</option>)}
        </Valaszto>
        {cel === 'szemle_egyeztetve' && (
          <Mezo label="Szemle időpontja" type="datetime-local" value={szemle} onChange={(e) => { setSzemle(e.target.value); onMentve(false); }} hiba={mezoHiba} />
        )}
        {cel === 'kesobb_aktualis' && (
          <>
            <Valaszto label="Mikor keressük újra?" value={honap} onChange={(e) => { setHonap(e.target.value); onMentve(false); }}>
              <option value="">Válassz hónapot…</option>
              {valaszthatoHonapok(Date.now()).map((h) => <option key={h.ertek} value={h.ertek}>{h.cimke}</option>)}
            </Valaszto>
            {mezoHiba && <p className="text-sm text-hiba">{mezoHiba}</p>}
          </>
        )}
        {cel === 'megbizas' && (
          <fieldset className="grid gap-2">
            <legend className="text-sm font-medium text-szoveg-halk">Megbízás típusa</legend>
            {Object.entries(MEGBIZAS_TIPUS_CIMKE).map(([kulcs, cimke]) => (
              <label key={kulcs} className="flex min-h-11 items-center gap-3 rounded-xl border border-vonal px-3.5">
                <input type="radio" name="megbizas" className="size-4 accent-kiemelt" checked={megbizas === kulcs} onChange={() => { setMegbizas(kulcs as StatuszMezok['megbizasTipus']); onMentve(false); }} />
                {cimke}
              </label>
            ))}
            {mezoHiba && <p className="text-sm text-hiba">{mezoHiba}</p>}
          </fieldset>
        )}
        {cel === 'eladva' && (
          <Mezo label="Tényleges eladási ár (Ft)" inputMode="numeric" value={ar} hiba={mezoHiba}
            onChange={(e) => { const n = szamBol(e.target.value); setAr(n ? n.toLocaleString('hu-HU') : ''); onMentve(false); }} />
        )}
        {cel === 'elveszett' && (
          <>
            <Valaszto label="Miért veszett el?" value={ok} onChange={(e) => { setOk(e.target.value); onMentve(false); }}>
              <option value="">Válassz okot…</option>
              {Object.entries(ELVESZETT_OK_CIMKE).map(([kulcs, cimke]) => <option key={kulcs} value={kulcs}>{cimke}</option>)}
            </Valaszto>
            {mezoHiba && <p className="text-sm text-hiba">{mezoHiba}</p>}
            <Mezo label="Megjegyzés (opcionális)" multiline value={megjegyzes} maxLength={4000} onChange={(e) => setMegjegyzes(e.target.value)} />
          </>
        )}
        <HibaUzenet hiba={mentes.error} />
        {mentve && <p role="status" className="rounded-xl border border-siker px-3.5 py-2.5 text-sm text-siker">Mentve.</p>}
        <Gomb toltes={mentes.isPending} disabled={cel === lead.statusz && cel !== 'szemle_egyeztetve' && cel !== 'kesobb_aktualis' && cel !== 'megbizas' && cel !== 'eladva' && cel !== 'elveszett'} onClick={mentesGomb}>
          Státusz mentése
        </Gomb>
      </div>
    </Kartya>
  );
}
